import React, { useState } from 'react';
import { toast } from 'sonner';
import { api, authUrl, fmtPrice } from '../../lib/api';
import { FileDown, Save, Eye, EyeOff } from 'lucide-react';
import { MoodboardEditor } from './MoodboardEditor';
import { ProductsEditor } from './ProductsEditor';
import { PaletteEditor } from './PaletteEditor';
import { TemplateMenu } from './TemplateMenu';

const FIELDS = ['moodboard_url', 'moodboard_images', 'items', 'notes', 'palette', 'include_discount'];
const pick = (p) => Object.fromEntries(FIELDS.map(k => [k, p[k]]));

export const Section = ({ title, children, testid, actions }) => (
  <section className="border border-stilo-ink/10 bg-white p-6 lg:p-8" data-testid={testid}>
    <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
      <p className="overline">{title}</p>
      {actions && <div className="flex gap-2">{actions}</div>}
    </div>
    {children}
  </section>
);

export const ProposalEditor = ({ cid, initial, chatImages, onPublished }) => {
  const [p, setP] = useState(initial);
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setP(prev => ({ ...prev, [k]: v }));
  const published = p.status === 'published';

  const save = async () => {
    setBusy(true);
    try {
      const r = await api.put(`/consultations/${cid}/proposal`, pick(p));
      setP(r.data); toast.success('Propuesta guardada');
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail?.[0]?.msg || e?.response?.data?.detail || 'No se pudo guardar');
      return false;
    } finally { setBusy(false); }
  };

  const togglePublish = async () => {
    if (!(await save())) return;
    try {
      const r = await api.post(`/consultations/${cid}/proposal/publish`, { published: !published });
      setP(r.data);
      onPublished?.();
      if (r.data.emailed === true) toast.success('Le he enviado un email a tu clienta');
      if (r.data.emailed === false) toast.error('Publicada, pero no se pudo enviar el email');
      toast.success(r.data.status === 'published' ? 'Publicada: tu clienta ya puede verla' : 'Vuelve a ser borrador');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo cambiar el estado');
    }
  };

  const total = p.items.reduce((s, i) => s + Number(i.price || 0) * Math.max(1, Number(i.qty || 1)), 0);

  return (
    <div className="space-y-6" data-testid="proposal-editor">
      <div className="flex flex-wrap items-center justify-between gap-4 border border-stilo-ink/10 bg-stilo-beige p-6">
        <div>
          <p className="overline">Estado</p>
          <p className="font-serif text-2xl mt-1" data-testid="proposal-status">{published ? 'Publicada · visible para la clienta' : 'Borrador · solo la ves tú'}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={save} disabled={busy} className="inline-flex items-center gap-2 px-4 py-2 border border-stilo-ink/20 text-sm hover:border-stilo-ink disabled:opacity-50" data-testid="proposal-save">
            <Save className="w-4 h-4" strokeWidth={1.5} /> Guardar
          </button>
          <a href={authUrl(`/consultations/${cid}/proposal/pdf`)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 px-4 py-2 border border-stilo-ink/20 text-sm hover:border-stilo-ink" data-testid="proposal-pdf-preview">
            <FileDown className="w-4 h-4" strokeWidth={1.5} /> Ver PDF
          </a>
          <button onClick={togglePublish} disabled={busy} className="btn-stilo disabled:opacity-50" data-testid="proposal-publish-toggle">
            {published ? <EyeOff className="w-4 h-4" strokeWidth={1.5} /> : <Eye className="w-4 h-4" strokeWidth={1.5} />}
            {published ? 'Despublicar' : 'Publicar'}
          </button>
        </div>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-xs text-stilo-ink2">El PDF usa lo último que hayas guardado. Al publicar por primera vez, tu clienta recibe un email.</p>
        <TemplateMenu kind="proposal" label="Plantillas de propuesta" testid="tpl-proposal" applyLabel="Cargar"
          getData={() => pick(p)}
          onApply={(d) => setP(prev => ({ ...prev, palette: d.palette, notes: d.notes, moodboard_url: d.moodboard_url,
            items: d.items.map(i => ({ ...i, id: crypto.randomUUID() })) }))} />
      </div>

      <MoodboardEditor cid={cid} url={p.moodboard_url} setUrl={set('moodboard_url')}
        images={p.moodboard_images} setImages={set('moodboard_images')} chatImages={chatImages} />
      <ProductsEditor items={p.items} setItems={set('items')} total={fmtPrice(total)} />
      <PaletteEditor palette={p.palette} setPalette={set('palette')} />

      <Section title="Notas y recomendaciones" testid="proposal-notes-section">
        <textarea value={p.notes} onChange={e => set('notes')(e.target.value)} rows={6}
          placeholder="Explica a tu clienta la idea de la propuesta…"
          className="w-full border border-stilo-ink/15 p-3 bg-stilo-bg focus:outline-none focus:border-stilo-ink" data-testid="proposal-notes" />
        <label className="flex items-center gap-3 mt-4 text-sm text-stilo-ink2">
          <input type="checkbox" checked={p.include_discount} onChange={e => set('include_discount')(e.target.checked)} data-testid="proposal-include-discount" />
          Incluir el código de descuento {p.discount_code && <strong className="text-stilo-ink">{p.discount_code}</strong>}
        </label>
      </Section>
    </div>
  );
};
